// WaafiPay Error Handler for Frontend
import React from 'react';
import { toast } from 'react-toastify';

// Cooldown management for WaafiPay retries
const cooldownTimers = new Map();

/**
 * Check if user is in cooldown period for WaafiPay retries
 * @param {string} userId - User ID
 * @returns {boolean} - Whether user is in cooldown
 */
export const isInCooldown = (userId) => {
  const timer = cooldownTimers.get(userId);
  if (!timer) return false;
  
  const now = Date.now();
  const timeRemaining = timer.endTime - now;
  
  if (timeRemaining <= 0) {
    cooldownTimers.delete(userId);
    return false;
  }
  
  return true;
};

/**
 * Get remaining cooldown time in seconds
 * @param {string} userId - User ID
 * @returns {number} - Remaining time in seconds
 */
export const getCooldownRemaining = (userId) => {
  const timer = cooldownTimers.get(userId);
  if (!timer) return 0;
  
  const now = Date.now();
  const timeRemaining = timer.endTime - now;
  
  if (timeRemaining <= 0) {
    cooldownTimers.delete(userId);
    return 0;
  }
  
  return Math.ceil(timeRemaining / 1000);
};

/**
 * Start cooldown timer for WaafiPay retries
 * @param {string} userId - User ID
 * @param {number} durationMs - Duration in milliseconds (default: 30000 for 30 seconds)
 */
export const startCooldown = (userId, durationMs = 30000) => {
  const endTime = Date.now() + durationMs;
  cooldownTimers.set(userId, { endTime });
  
  // Clean up timer after duration
  setTimeout(() => {
    cooldownTimers.delete(userId);
  }, durationMs);
};

/**
 * Clear cooldown timer for a user (e.g., when payment succeeds)
 * @param {string} userId - User ID
 */
export const clearCooldown = (userId) => {
  cooldownTimers.delete(userId);
};

// Error code mappings for user-friendly messages
const ERROR_MESSAGES = {
  INSUFFICIENT_FUNDS: {
    title: 'Insufficient Funds',
    message: 'Your WaafiPay account doesn\'t have enough funds to complete this payment.',
    action: 'Please add money to your WaafiPay account and try again. Please wait 30 seconds between attempts.',
    severity: 'warning',
    icon: '💰',
    requiresCooldown: true
  },
  INVALID_PHONE: {
    title: 'Invalid Phone Number',
    message: 'The phone number provided is not valid or not registered with WaafiPay.',
    action: 'Please verify your phone number is registered with WaafiPay and try again.',
    severity: 'error',
    icon: '📱'
  },
  ACCOUNT_NOT_FOUND: {
    title: 'Account Not Found',
    message: 'WaafiPay account not found for the provided phone number.',
    action: 'Please register your phone number with WaafiPay or contact support.',
    severity: 'error',
    icon: '❌'
  },
  TIMEOUT: {
    title: 'Payment Timeout',
    message: 'The payment request timed out. Please try again.',
    action: 'Retry the payment or contact support if the issue persists. Please wait 30 seconds between attempts.',
    severity: 'warning',
    icon: '⏰',
    requiresCooldown: true
  },
  USER_CANCELLED: {
    title: 'Payment Cancelled',
    message: 'The payment was cancelled. You can try again when ready.',
    action: 'Retry the payment when you are ready to proceed.',
    severity: 'info',
    icon: '🚫'
  },
  NETWORK_ERROR: {
    title: 'Network Error',
    message: 'Network connection error. Please check your internet connection.',
    action: 'Check your internet connection and retry the payment. Please wait 30 seconds between attempts.',
    severity: 'warning',
    icon: '🌐',
    requiresCooldown: true
  },
  SERVICE_UNAVAILABLE: {
    title: 'Service Unavailable',
    message: 'WaafiPay service is temporarily unavailable. Please try again later.',
    action: 'Wait a few minutes and try again, or contact support. Please wait 30 seconds between attempts.',
    severity: 'warning',
    icon: '🔧',
    requiresCooldown: true
  },
  INVALID_AMOUNT: {
    title: 'Invalid Amount',
    message: 'The payment amount is invalid. Please contact support.',
    action: 'Contact support to resolve this issue.',
    severity: 'error',
    icon: '💳'
  },
  DUPLICATE_TRANSACTION: {
    title: 'Duplicate Transaction',
    message: 'This payment has already been processed. Please check your subscription status.',
    action: 'Check if your subscription is already active.',
    severity: 'info',
    icon: '🔄'
  },
  UNKNOWN_ERROR: {
    title: 'Payment Error',
    message: 'An unexpected error occurred during payment processing.',
    action: 'Retry the payment or contact support if the issue persists. Please wait 30 seconds between attempts.',
    severity: 'error',
    icon: '❓',
    requiresCooldown: true
  }
};

/**
 * Parse error response from backend and return structured error information
 * @param {Object|string} error - Error object or string from API response
 * @returns {Object} Structured error information
 */
export const parsePaymentError = (error) => {
  // Handle different error formats
  let errorData = null;
  
  if (typeof error === 'string') {
    try {
      errorData = JSON.parse(error);
    } catch {
      // If it's not JSON, treat as a simple error message
      return {
        ...ERROR_MESSAGES.UNKNOWN_ERROR,
        originalMessage: error
      };
    }
  } else if (error && typeof error === 'object') {
    errorData = error;
  } else {
    return ERROR_MESSAGES.UNKNOWN_ERROR;
  }

  // Check if it's a structured error response from our backend
  if (errorData.error && errorData.error.code) {
    const errorCode = errorData.error.code;
    const errorInfo = ERROR_MESSAGES[errorCode] || ERROR_MESSAGES.UNKNOWN_ERROR;
    
    return {
      ...errorInfo,
      code: errorCode,
      originalMessage: errorData.error.originalMessage || errorData.error.message,
      userAction: errorData.error.userAction || errorInfo.action,
      severity: errorData.error.severity || errorInfo.severity
    };
  }

  // Handle legacy error format
  if (errorData.message) {
    const message = errorData.message.toLowerCase();
    
    if (message.includes('insufficient') || message.includes('kuguma filna') || message.includes('haraaga')) {
      return {
        ...ERROR_MESSAGES.INSUFFICIENT_FUNDS,
        originalMessage: errorData.message
      };
    }
    
    if (message.includes('phone') || message.includes('mobile')) {
      return {
        ...ERROR_MESSAGES.INVALID_PHONE,
        originalMessage: errorData.message
      };
    }
    
    if (message.includes('timeout') || message.includes('expired')) {
      return {
        ...ERROR_MESSAGES.TIMEOUT,
        originalMessage: errorData.message
      };
    }
    
    if (message.includes('cancelled') || message.includes('canceled')) {
      return {
        ...ERROR_MESSAGES.USER_CANCELLED,
        originalMessage: errorData.message
      };
    }
    
    if (message.includes('network') || message.includes('connection')) {
      return {
        ...ERROR_MESSAGES.NETWORK_ERROR,
        originalMessage: errorData.message
      };
    }
  }

  return {
    ...ERROR_MESSAGES.UNKNOWN_ERROR,
    originalMessage: errorData.message || JSON.stringify(errorData)
  };
};

/**
 * Display error toast notification
 * @param {Object} errorInfo - Parsed error information
 * @param {Function} onAction - Optional callback for action button
 * @param {string} userId - User ID for cooldown management
 */
export const showPaymentError = (errorInfo, onAction = null, userId = null) => {
  const { title, message, action, severity, icon } = errorInfo;
  
  const toastOptions = {
    position: 'top-center',
    autoClose: 8000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: 'light',
    style: {
      background: severity === 'error' ? '#ffffff' : 
                  severity === 'warning' ? '#ffffff' : 
                  severity === 'info' ? '#ffffff' : '#ffffff',
      color: severity === 'error' ? '#dc2626' : 
             severity === 'warning' ? '#d97706' : 
             severity === 'info' ? '#2563eb' : '#374151',
      border: severity === 'error' ? '1px solid #fecaca' : 
              severity === 'warning' ? '1px solid #fed7aa' : 
              severity === 'info' ? '1px solid #bfdbfe' : '1px solid #e5e7eb',
      maxWidth: '500px',
      padding: '20px',
      borderRadius: '16px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      fontFamily: 'DM Sans, sans-serif'
    }
  };

  const toastContent = (
    <div className="flex flex-col gap-3 min-w-0">
      <div className="flex items-center gap-3">
        <span className="text-2xl flex-shrink-0">{icon}</span>
        <span className="font-bold text-lg text-navy-700 dark:text-white truncate">{title}</span>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed break-words">{message}</p>
      {action && (
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400 flex-1 min-w-0">{action}</span>
          {onAction && userId && errorInfo.requiresCooldown && (
            <CooldownRetryButton onAction={onAction} userId={userId} />
          )}
          {onAction && (!userId || !errorInfo.requiresCooldown) && (
            <button
              onClick={onAction}
              className="text-xs px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all duration-200 font-medium shadow-sm flex-shrink-0 whitespace-nowrap"
            >
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  );

  toast(toastContent, toastOptions);
};

// Cooldown Retry Button Component
const CooldownRetryButton = ({ onAction, userId }) => {
  const [timeRemaining, setTimeRemaining] = React.useState(getCooldownRemaining(userId));
  const [isInCooldownState, setIsInCooldownState] = React.useState(isInCooldown(userId));

  React.useEffect(() => {
    // Check cooldown status every second
    const interval = setInterval(() => {
      const remaining = getCooldownRemaining(userId);
      const inCooldown = isInCooldown(userId);
      
      setTimeRemaining(remaining);
      setIsInCooldownState(inCooldown);
      
      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [userId]);

  const handleRetry = () => {
    if (isInCooldownState) return;
    
    // Don't start cooldown here - it should already be started when error occurred
    onAction();
  };

  if (isInCooldownState) {
    return (
      <button
        disabled
        className="text-xs px-4 py-2 bg-gray-400 text-white rounded-lg font-medium shadow-sm flex-shrink-0 whitespace-nowrap cursor-not-allowed"
      >
        Wait {timeRemaining}s
      </button>
    );
  }

  return (
    <button
      onClick={handleRetry}
      className="text-xs px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all duration-200 font-medium shadow-sm flex-shrink-0 whitespace-nowrap"
    >
      Retry
    </button>
  );
};

/**
 * Get error message for display in components
 * @param {Object|string} error - Error from API response
 * @returns {string} User-friendly error message
 */
export const getErrorMessage = (error) => {
  const errorInfo = parsePaymentError(error);
  return errorInfo.message;
};

/**
 * Get user action message for display in components
 * @param {Object|string} error - Error from API response
 * @returns {string} User action message
 */
export const getUserAction = (error) => {
  const errorInfo = parsePaymentError(error);
  return errorInfo.userAction || errorInfo.action;
};

/**
 * Check if error is retryable
 * @param {Object|string} error - Error from API response
 * @returns {boolean} Whether the error is retryable
 */
export const isRetryableError = (error) => {
  const errorInfo = parsePaymentError(error);
  const retryableCodes = ['INSUFFICIENT_FUNDS', 'TIMEOUT', 'NETWORK_ERROR', 'SERVICE_UNAVAILABLE', 'UNKNOWN_ERROR'];
  return retryableCodes.includes(errorInfo.code);
}; 